import {AppUser} from "../models/app";
import fb from "./firebase";
import firebase from "firebase/compat/app";
import {BaseModel} from "../models/Core";

export interface ChoiceField {
  _choices: {[key: string]: string};
  getChoices: () => {value: string, label: string}[];
  getLabel: (value: string) => string | undefined;
  getValue: (label: string) => string | undefined;
  getValues: () => string[];
}

interface ErrorMap {
  [field: string]: string[]
}

export class ModelErrorMap {
  map: ErrorMap;

  constructor() {
    this.map = {};
  }

  hasErrors(): boolean {
    return Object.keys(this.map).length > 0;
  }

  addError(field: string, error: string) {
    if (!this.map[field])
      this.map[field] = [];

    this.map[field].push(error);
  }

  getErrors(field: string): string[] {
    return this.map[field] || [];
  }

  getMap(): ErrorMap {
    return this.map;
  }

  getFlat(): string[] {
    return Object.keys(this.map).map(field => {
      return this.map[field].map(error => `${field}: ${error}`);
    }).flat();
  }
}


interface ModelCleanResult {
  success: boolean;
  errors: ModelErrorMap;
}

export abstract class ModelControler<M extends BaseModel> {
  private protectedAttrs = ['id', 'path', 'type', 'criado_em', 'atualizado_em'];

  private instance?: M = undefined;
  private values: Partial<M>;
  private validated: boolean = false;
  protected changed: boolean = false;
  private isValid: boolean = false;
  readonly user: AppUser;
  private userRef?: firebase.firestore.DocumentReference | undefined;

  constructor(user: AppUser) {
    this.user = user;
    this.values = {};
  }

  get(path: keyof M): any {
    return this.getValue(path);
  }

  getValue(path: keyof M): any {
    if (this.values.hasOwnProperty(path)) {
      return this.values[path];
    }
    if (this.instance) {
      return this.instance[path];
    }
  }

  getFromValues(path: keyof M): any {
    if (this.values.hasOwnProperty(path)) {
      return this.values[path];
    }
    return null;
  }

  getFromInstance(path: keyof M): any {
    if (this.instance && this.instance.hasOwnProperty(path)) {
      return this.instance[path];
    }
    return null;
  }

  getRef(): firebase.firestore.DocumentReference {
    if (!this.instance) {
      throw new Error('Model has no instance');
    }

    const {path} = this.instance;
    if (!path) {
      console.log('==> Model has no path', this.instance);
      throw new Error('Model has no path');
    }

    return fb.db.doc(this.instance.path);
  }

  setValue(key: keyof M, value: any) {
    if (this.protectedAttrs.indexOf(key as string) !== -1)
      throw new Error(`Cannot set protected attribute ${key.toString()}`);

    const _valV = this.getFromValues(key);
    const _valI = this.getFromInstance(key);

    // sem alteracao
    if (_valV !== null && _valV === value)
      return;

    // sem alteracao em relacao a instance
    if (_valV === null && _valI !== null && _valI === value)
      return;

    // update values
    if (
      (_valV === null && _valI === null) ||
      (_valV === null && _valI !== null && _valI !== value) ||
      (_valV !== null && _valV !== value && _valI !== null && _valI !== value)
    ) {
      this.values[key] = value;
      this.changed = true;
      this.validated = false;
      this.isValid = false;
      return;
    }

    // resetar key in values se value == instance
    if (_valV !== null && _valV !== value && _valI !== null && _valI === value) {
      delete this.values[key];
      return;
    }

    throw new Error('Invalid state');
  }

  set(key: keyof M, value: any) {
    this.setValue(key, value);
  }

  setInstance(instance: M) {
    const {id, path, type} = instance;
    if (!id || !path || !type) {
      throw new Error('Invalid instance');
    }

    this.instance = this.prepareInstance(instance);
    this.values = {};
    this.validated = true;
    this.isValid = true;
    this.changed = false;
  }

  setValues(values: Partial<M>) {
    Object.keys(values).forEach(key => {
      this.setValue(key as keyof M, values[key as keyof M]);
    });
  }

  get isNew(): boolean {
    return this.instance === undefined;
  }

  getInstance(): M {
    let res: M;
    if (this.instance)
      res = {...this.instance, ...this.values};
    else
      res = {...this.values} as M;

    // filtrar protected attrs
    this.protectedAttrs.forEach((attr) => {
      delete res[attr as keyof BaseModel];
    });
    return res;
  }

  loadUser(): firebase.firestore.DocumentReference {
    if (!this.userRef) {
      this.userRef = fb.db.doc(`users/${this.user.uid}`);
    }

    return this.userRef;
  }

  clean(): ModelCleanResult {
    const errors = this.onClean();
    this.validated = true;
    this.isValid =  !errors || !errors.hasErrors();

    return {
      success: this.isValid,
      errors: errors,
    };
  }

  async save(): Promise<firebase.firestore.DocumentReference> {
    if (!this.validated)
      this.clean();

    if (!this.isValid)
      throw new Error('Model is not valid');

    const toSave = await this.preSave();
    return this.onSave(toSave);
  }

  prepareInstance(instance: M): M {
    // ajustar valores carregados do firebase
    return {...instance};
  }

  abstract onClean(): ModelErrorMap;
  abstract preSave(): Promise<M>;
  abstract onSave(values: M): Promise<firebase.firestore.DocumentReference>;
}